/**
 * shortcuts — the SOURCE OF TRUTH for the Visual Query Editor keyboard-shortcut
 * help. Every combo is written once in chord notation and rendered by the
 * app's one chord formatter, so the list reads `Ctrl+Z` / `⌘Z` like the rest
 * of the app instead of mixing a "Mod + Z" spelling with platform chords. Buttons (Edit as
 * SQL) are not listed — this is a list of keys. Plain, descriptive copy (no internal codenames) lives here rather than
 * in vqeCopy: the shortcut list is a self-contained reference that the "?"
 * help dialog (VqeShortcutsHelp) and the future public docs page (owned by
 * Phase D) both read from, so a single edit here keeps every surface in sync.
 *
 * spec: docs/superpowers/specs/2026-06-14-vqe-ux-refinement-design.md
 *   § Keyboard Shortcut Help.
 */

import { getShortcutLabel } from '../../../../features/keybindings/hooks/useShortcutLabel';
import { formatChord } from '../../../../features/keybindings/model/chord';
import { platform } from '../../../../features/keybindings/model/platform';
import type {
  ChordModifier,
  KeybindingCommandId,
  Platform,
} from '../../../../features/keybindings/model/keybindingTypes';

export type VqeShortcutKeybindingId = Extract<
  KeybindingCommandId,
  'global.openVisualQueryEditor' | 'vqe.execute'
>;

/**
 * A fixed (not rebindable) key combo. Structured rather than a chord string
 * like the registry's: the keybinding anti-drift guard keeps bindable chord
 * strings out of this module, and these keys are not bindable.
 */
export interface ShortcutChord {
  readonly modifiers?: readonly ChordModifier[];
  /** Key token as `formatChord` expects it — `Z`, `Tab`, `Enter`, `Escape`, `Space`, `?`. */
  readonly key: string;
}

export interface ShortcutItem {
  /**
   * Fixed key combos, rendered for the platform by the same formatter the
   * Settings → Keyboard Shortcuts tab uses (`formatChord`): `Ctrl+Z` / `⌘Z`.
   * Alternatives are listed separately and shown as "A / B".
   */
  readonly chords?: readonly ShortcutChord[];
  /** Plain text for keys that are not one combo, e.g. "Arrow keys". */
  readonly text?: string;
  /** What the combo does, in plain language. */
  readonly description: string;
  /** Registry binding used instead of `chords` for rebindable commands. */
  readonly keybindingId?: VqeShortcutKeybindingId;
}

export interface ShortcutGroup {
  /** Section heading, e.g. "Edit". Also drives the group test id. */
  readonly section: string;
  readonly items: readonly ShortcutItem[];
}

/**
 * Built directly rather than through `parseChord`, whose typing guard rejects
 * bare keys (`Enter`, `?`) — those are not bindable, but they are still keys
 * the help must show.
 */
function toChord(chord: ShortcutChord) {
  return { modifiers: chord.modifiers ?? [], key: chord.key, physical: false };
}

const key = (k: string): ShortcutChord => ({ key: k });

/** The keys column for one item, formatted for `targetPlatform`. */
export function shortcutKeysLabel(item: ShortcutItem, targetPlatform: Platform = platform()): string {
  if (item.keybindingId) return getShortcutLabel(item.keybindingId, targetPlatform);
  if (item.chords) {
    return item.chords.map((chord) => formatChord(toChord(chord), targetPlatform)).join(' / ');
  }
  return item.text ?? '';
}

export const shortcuts: readonly ShortcutGroup[] = [
  {
    section: 'Open',
    items: [
      { chords: [key('?')], description: 'Show this keyboard-shortcut help' },
      {
        keybindingId: 'global.openVisualQueryEditor',
        description: 'Switch the query tab between its SQL and Visual views (or open a new one in Visual)',
      },
    ],
  },
  {
    section: 'Navigate',
    items: [
      { chords: [key('Tab')], description: 'Move focus to the next control' },
      { chords: [{ modifiers: ['Shift'], key: 'Tab' }], description: 'Move focus to the previous control' },
      { chords: [key('Enter')], description: 'Activate the focused control or open a list' },
    ],
  },
  {
    section: 'Edit',
    items: [
      { chords: [key('Enter')], description: 'Apply the focused filter or value edit' },
      { chords: [key('Escape')], description: 'Cancel the open editor and discard the change' },
      {
        chords: [key('Space'), key('Enter')],
        description: 'Pick up or drop a SELECT or GROUP BY chip to reorder it',
      },
      { text: 'Arrow keys', description: 'Move the picked-up chip to a new position' },
      { chords: [key('Escape')], description: 'Cancel the reorder and keep the original order' },
    ],
  },
  {
    section: 'Undo',
    items: [
      { chords: [{ modifiers: ['Mod'], key: 'Z' }], description: 'Undo the last edit to the SQL' },
      { chords: [{ modifiers: ['Mod', 'Shift'], key: 'Z' }], description: 'Redo the last undone edit' },
    ],
  },
  {
    section: 'Statement picker',
    items: [
      { chords: [key('Enter')], description: 'Open the statement picker dropdown' },
      { text: 'Arrow keys', description: 'Move between statements in the picker' },
      { chords: [key('Escape')], description: 'Close the statement picker without changing' },
    ],
  },
  {
    section: 'Run',
    items: [
      {
        keybindingId: 'vqe.execute',
        description: 'Execute the current statement',
      },
    ],
  },
] as const;
